/* eslint-disable react-hooks/exhaustive-deps */
import React, { useLayoutEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Input,
  Popconfirm,
  Modal,
  Descriptions,
} from "antd";
import { PlusOutlined, EyeOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import { getAPI, postAPI, displayMessage } from "../../utils/common";
import { SUCCESS_MESSAGE, ERROR_MSG_TYPE } from "../../constants/messages";
import { KITCHEN_ISSUE_LIST, KITCHEN_ISSUE_DELETE } from "../../constants/api";

function KitchenIssueTable() {
  const { Content } = Layout;
  const navigate = useNavigate();

  const [state, setState] = useState([]);
  const [filteredState, setFilteredState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  const printRef = useRef();

  useLayoutEffect(() => {
    getList();
  }, []);

  const getList = () => {
    setLoading(true);
    const successFn = (res) => {
      if (res.status === true) {
        const formatted = res.data.map((row, index) => ({
          key: index,
          ID: index + 1,
          Date: dayjs(row.issue_date).format("DD MMM YYYY HH:mm"),
          Items: row.items?.length || 0,
          Remark: row.remark || "-",
          HandOverTo: row.hand_over_to || "-",
          RawItems: row.items,
          Action: (
            <Space>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openViewModal(row)}
              >
                View
              </Button>
              {/* <Popconfirm
                title="Delete Issue"
                description="This will reverse stock. Continue?"
                onConfirm={() => deleteData(row.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button size="small" danger>
                  Delete
                </Button>
              </Popconfirm> */}
            </Space>
          ),
        }));
        setState(formatted);
        setFilteredState(formatted);
      } else {
        setState([]);
        setFilteredState([]);
      }
      setLoading(false);
    };
    const errorFn = () => {
      setLoading(false);
      setState([]);
      setFilteredState([]);
    };
    getAPI(KITCHEN_ISSUE_LIST, successFn, errorFn, {});
  };

  const deleteData = (id) => {
    setLoading(true);
    const successFn = (res) => {
      if (res.status === true) {
        displayMessage(SUCCESS_MESSAGE, res.message);
        getList();
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, res.message);
      }
    };
    const errorFn = () => {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to delete issue");
    };
    postAPI(KITCHEN_ISSUE_DELETE, { id }, successFn, errorFn);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = state.filter((row) =>
      row.Remark.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredState(filtered);
  };

  const openViewModal = (row) => {
    setSelectedIssue(row);
    setViewModalVisible(true);
  };

  const closeViewModal = () => {
    setViewModalVisible(false);
    setSelectedIssue(null);
  };

  // 🔹 Native Print
  const handlePrint = () => {
    if (!printRef.current) return;
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload(); // reload to restore React app
  };

  const columns = [
    { title: "S.No", dataIndex: "ID", width: 60 },
    { title: "Issue Date", dataIndex: "Date", width: 180 },
    { title: "Total Items", dataIndex: "Items", width: 120 },
    { title: "Remark", dataIndex: "Remark", width: 250 },
    { title: "Action", dataIndex: "Action", width: 180 },
  ];

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Item Issue List"
          bordered
          extra={
            <Space>
              <Input
                placeholder="Search by remark"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Button
                type="primary"
                onClick={() => navigate("/kitchen/issue/add")}
              >
                Add Issue <PlusOutlined />
              </Button>
            </Space>
          }
        >
          <Table
            loading={loading}
            columns={columns}
            dataSource={filteredState}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 800 }}
          />
        </Card>

        {/* View Modal */}
        <Modal
          title="Kitchen Issue Details"
          visible={viewModalVisible}
          onCancel={closeViewModal}
          width={1000}
          footer={[
            <Button
              key="print"
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print
            </Button>,
            <Button key="close" onClick={closeViewModal}>
              Close
            </Button>,
          ]}
        >
          {selectedIssue && (
            <div ref={printRef}>
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Issue Date & Time">
                  {dayjs(selectedIssue.issue_date).format("DD MMM YYYY HH:mm")}
                </Descriptions.Item>
                <Descriptions.Item label="Hand Over To">
                  {selectedIssue.hand_over_to}
                </Descriptions.Item>
                <Descriptions.Item label="Issue By">
                  {selectedIssue.issue_by}
                </Descriptions.Item>
                <Descriptions.Item label="Remark">
                  {selectedIssue.remark}
                </Descriptions.Item>
                <Descriptions.Item label="Items">
                  <Table
                    size="small"
                    columns={[
                      { title: "S.No", dataIndex: "ID", width: 50 },
                      { title: "Code", dataIndex: "code" },
                      { title: "Item Name", dataIndex: "name" },
                      { title: "Quantity", dataIndex: "qty" },
                      { title: "Purpose", dataIndex: "purpose" },
                    ]}
                    dataSource={selectedIssue.items?.map((item, idx) => ({
                      key: idx,
                      ID: idx + 1,
                      code: item.item?.item_code,
                      name: item.item?.name,
                      qty: `${
                        Number.isInteger(parseFloat(item.qty))
                          ? parseFloat(item.qty).toFixed(0)
                          : parseFloat(item.qty).toFixed(2)
                      } ${item.item?.unit || ""}`,
                      purpose: item.purpose,
                    }))}
                    pagination={false}
                  />
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
        </Modal>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenIssueTable;
